import { EllipsisVerticalIcon, MailIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../../../components/ui/button.tsx';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { RouterOutputs } from '../../../utils/api.ts';
import { api } from '../../../utils/api.ts';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { DeleteEventModal } from './delete-event-modal.tsx';

type EventOptionsDropdownProps = {
    date: string;
    event: RouterOutputs['platoUser']['query']['events']['getEventById'];
    onEdit?: () => void;
    onDelete?: () => void;
};

export const EventOptionsDropdown = ({ date, event, onEdit, onDelete }: EventOptionsDropdownProps) => {
    const { t } = useLocale('events');

    const sendInvitation = api.platoUser.command.events.sendEventInvitation.useMutation();

    const handleDeleteClick = () => {
        DialogStateManager.show(DeleteEventModal, {
            date,
            event,
            onDeleted: onDelete,
        });
    };

    const handleResendInviteClick = () => {
        if (!event.event_id) {
            return;
        }

        sendInvitation.mutate({
            eventId: event.event_id,
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="icon"
                    icon={EllipsisVerticalIcon}
                    aria-label={t('more_options', { ns: 'common' })}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
                {onEdit && (
                    <DropdownMenuItem onSelect={onEdit}>
                        <PencilIcon className="h-4 w-4" />
                        <Text as="span">{t('events__edit', { ns: 'events' })}</Text>
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={handleResendInviteClick}>
                    <MailIcon className="h-4 w-4" />
                    <Text as="span">{t('events__resend_invite', { ns: 'events' })}</Text>
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleDeleteClick}>
                    <Trash2Icon className="h-4 w-4 text-red-700" />
                    <Text as="span" color="red-600">
                        {t('events__delete', { ns: 'events' })}
                    </Text>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
